import { useEffect, useLayoutEffect, useRef, useState, type CSSProperties, type RefObject } from 'react';

/**
 * Fixed-position coordinates for a panel anchored under (or, when there is no
 * room, above) a trigger. Panels are portaled to <body> so a card's
 * overflow:hidden or a modal's scroll area can never clip them.
 */
export function useAnchoredStyle(
  open: boolean,
  anchorRef: RefObject<HTMLElement | null>,
  { gap = 6, maxHeight = 290, minWidth, maxWidth = 340, align = 'start' }: {
    gap?: number;
    maxHeight?: number;
    minWidth?: number | 'anchor';
    maxWidth?: number;
    align?: 'start' | 'end';
  } = {},
): CSSProperties | null {
  const [style, setStyle] = useState<CSSProperties | null>(null);

  useLayoutEffect(() => {
    if (!open) {
      setStyle(null);
      return;
    }
    const update = () => {
      const el = anchorRef.current;
      if (!el) return;
      const r = el.getBoundingClientRect();
      const vw = window.innerWidth;
      const vh = window.innerHeight;
      const below = vh - r.bottom - gap - 8;
      const above = r.top - gap - 8;
      const openUp = below < Math.min(maxHeight, 220) && above > below;
      const avail = Math.max(120, Math.min(maxHeight, openUp ? above : below));
      const width = Math.min(maxWidth, vw - 16);
      const s: CSSProperties = {
        position: 'fixed',
        zIndex: 85,
        maxHeight: avail,
        maxWidth: width,
        minWidth: minWidth === 'anchor' ? Math.min(r.width, width) : minWidth,
      };
      if (openUp) s.bottom = vh - r.top + gap;
      else s.top = r.bottom + gap;
      if (align === 'end') s.right = Math.max(8, vw - r.right);
      else s.left = Math.max(8, Math.min(r.left, vw - Math.min(width, Math.max(r.width, 200)) - 8));
      setStyle(s);
    };
    update();
    window.addEventListener('scroll', update, true);
    window.addEventListener('resize', update);
    return () => {
      window.removeEventListener('scroll', update, true);
      window.removeEventListener('resize', update);
    };
  }, [open, anchorRef, gap, maxHeight, minWidth, maxWidth, align]);

  return style;
}

/** Calls `onOutside` on a pointer press outside every given element. */
export function useOutsidePress(
  open: boolean,
  refs: Array<RefObject<HTMLElement | null>>,
  onOutside: () => void,
) {
  // Read the latest refs/callback at event time without re-binding listeners.
  const latest = useRef({ refs, onOutside });
  latest.current = { refs, onOutside };

  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent | TouchEvent) => {
      const t = e.target as Node;
      if (latest.current.refs.some((r) => r.current?.contains(t))) return;
      latest.current.onOutside();
    };
    document.addEventListener('mousedown', handler);
    document.addEventListener('touchstart', handler);
    return () => {
      document.removeEventListener('mousedown', handler);
      document.removeEventListener('touchstart', handler);
    };
  }, [open]);
}
